@file:OptIn(ExperimentalAnimationApi::class)

package demos.buttons

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.LocalContentColor
import androidx.compose.material.LocalTextStyle
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch


const val HOLD_TO_CONFIRM = "461b275062a95db9f765e60972bdd12e56f999cb2f8eb5f57892f1b3f513ecc2"


private sealed class HoldState {
    object Normal : HoldState()
    object Holding : HoldState()
    object Done : HoldState()
}

private val red = Color(0xfff21637)

@Composable
fun HoldToConfirm() {
    var touched by remember { mutableStateOf(false) }
    val scale by animateFloatAsState(
        targetValue = if (touched) .94f else 1f, animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessMediumLow
        )
    )

    val scope = rememberCoroutineScope()
    var holdState: HoldState by remember { mutableStateOf(HoldState.Normal) }
    val animatedProgress = remember { Animatable(0f) }
    val shakeAnimation = remember { Animatable(0f) }

    LaunchedEffect(touched) {
        if (touched) {
            holdState = HoldState.Holding
            val reason = animatedProgress.animateTo(1f, tween(1_000, easing = LinearEasing)).endReason
            if (reason == AnimationEndReason.Finished) {
                scope.launch {
                    holdState = HoldState.Done // Action confirmed
                    for (i in 0..2) {
                        shakeAnimation.animateTo(1f, spring(stiffness = 50_000f))
                        shakeAnimation.animateTo(-1f, spring(stiffness = 50_000f))
                    }
                    shakeAnimation.animateTo(0f)
                    delay(1_000)
                    animatedProgress.animateTo(0f, spring())
                    holdState = HoldState.Normal
                }
            }
        } else if (holdState != HoldState.Done) {
            holdState = HoldState.Normal
            animatedProgress.animateTo(0f, spring())
        }
    }

    Box(
        modifier = Modifier
            .padding(4.dp)
            .offset(x = shakeAnimation.value.dp)
            .pointerInput(Unit) {
                detectTapGestures(
                    onPress = {
                        if (holdState == HoldState.Normal) {
                            touched = true
                            awaitRelease()
                            touched = false
                        }
                    }
                )
            }
            .scale(scale)
            .border(
                width = 1.dp,
                color = red,
                shape = RoundedCornerShape(10.dp)
            )
            .clip(RoundedCornerShape(10.dp))
            .padding(4.dp)
            .clip(RoundedCornerShape(6.dp))
            .background(red.copy(alpha = .2f))
            .height(IntrinsicSize.Min)
            .width(IntrinsicSize.Min)
            .defaultMinSize(minWidth = 200.dp),
        contentAlignment = Alignment.Center,
    ) {
        Box(
            Modifier
                .align(Alignment.CenterStart)
                .fillMaxHeight()
                .fillMaxWidth(animatedProgress.value)
                .background(red, RoundedCornerShape(6.dp))
        )
        Box(
            modifier = Modifier
                .padding(horizontal = 24.dp, vertical = 8.dp),
            contentAlignment = Alignment.Center,
        ) {
            CompositionLocalProvider(
                LocalContentColor provides Color.White,
                LocalTextStyle provides TextStyle(fontWeight = FontWeight.Medium)
            ) {
                val springSpec: FiniteAnimationSpec<IntOffset> = remember {
                    spring(
                        dampingRatio = Spring.DampingRatioMediumBouncy,
                        stiffness = Spring.StiffnessMedium
                    )
                }
                AnimatedContent(
                    targetState = holdState,
                    transitionSpec = {
                        slideInVertically(
                            springSpec
                        ) { -it } + fadeIn(spring(stiffness = Spring.StiffnessHigh)) with slideOutVertically(
                            springSpec
                        ) { it } + fadeOut(spring(stiffness = Spring.StiffnessHigh)) using SizeTransform(clip = false)
                    },
                    contentAlignment = Alignment.Center,
                ) { holdState ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        when (holdState) {
                            HoldState.Normal -> {
                                Text(text = "Delete")
                            }

                            HoldState.Holding -> {
                                Text(text = "Hold to confirm")
                            }

                            HoldState.Done -> {
                                Text(text = "Deleted")
                                Image(
                                    modifier = Modifier.padding(start = 5.dp).size(16.dp),
                                    imageVector = Icons.Default.Delete,
                                    colorFilter = ColorFilter.tint(Color.White),
                                    contentDescription = "Deleted"
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}